import { Box, FaceLandmarks68 } from '@vladmandic/face-api';
import { useEffect, useRef } from 'react';

import { FaceApiClient } from 'lib/clients/FaceApiClient';
import { createCanvas } from 'lib/utils/canvas';
import { horizontalAngleFromLandmarks } from 'verify/scanners/SelfieScanner/client';

// Params chosen here:
// https://www.notion.so/withpersona/Tighten-TinyFaceDetector-options-for-SelfieScanner-vs-GovernmentIdScanner-8afcf7e20823478aa25ea95a60da00d2
const FACE_DETECTION_INPUT_SIZE = 320;
const FACE_DETECTION_THRESHOLD = 0.4;

interface PoseDetectionData {
  canvas: HTMLCanvasElement;
  faceBox: Box | undefined;
  faceCount: number;
  faceDimensions: {
    width: number;
    height: number;
  };
  horizontalAngle: number;
  landmarks: FaceLandmarks68 | null;
}

interface UsePoseDetectionModelReturn {
  canRunPoseDetection: (video: HTMLVideoElement) => boolean;
  getPoseDetectionData: (video: HTMLVideoElement) => Promise<PoseDetectionData | null>;
  getCanvasFromVideo: (video: HTMLVideoElement) => HTMLCanvasElement | null;
}

export const usePoseDetectionModel = ({
  handleFailed,
  handleModelLoaded,
}: {
  handleFailed: (error: Error | string, state: 'load' | 'capture') => void;
  handleModelLoaded: () => Promise<void>;
}): UsePoseDetectionModelReturn => {
  // These have to be refs because it will be accessed within a timeout
  const faceCanvasBufferRef = useRef<HTMLCanvasElement | null>(null);
  const canvasBufferRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const loadFaceApi = async () => {
      try {
        await FaceApiClient.loadModels();
        await handleModelLoaded();
      } catch (err) {
        handleFailed(err as unknown as string | Error, 'load');
        return;
      }
    };

    loadFaceApi();
  }, []);

  useEffect(() => {
    const [faceCanvasBuffer, cleanupFaceCanvasBuffer] = createCanvas();
    faceCanvasBufferRef.current = faceCanvasBuffer;
    const [canvasBuffer, cleanupCanvasBuffer] = createCanvas();
    canvasBufferRef.current = canvasBuffer;

    return () => {
      cleanupFaceCanvasBuffer();
      cleanupCanvasBuffer();
    };
  }, []);

  return {
    /**
     * Return a canvas buffer of the video frame -- mainly used for manual capture
     * @param video
     * @returns
     */
    getCanvasFromVideo: (video: HTMLVideoElement): HTMLCanvasElement | null => {
      const { videoWidth, videoHeight } = video;

      drawImageToCanvas({
        source: {
          element: video,
          height: videoHeight,
          width: videoWidth,
        },
        destination: {
          canvas: canvasBufferRef.current,
          height: videoHeight,
          width: videoWidth,
        },
      });

      return canvasBufferRef.current;
    },
    canRunPoseDetection: (video: HTMLVideoElement): boolean => {
      return FaceApiClient.isLoaded() && FaceApiClient.isMediaLoaded(video);
    },
    getPoseDetectionData: async (video: HTMLVideoElement): Promise<PoseDetectionData | null> => {
      if (video == null || canvasBufferRef.current == null) {
        return null;
      }

      const { videoWidth, videoHeight } = video;

      drawImageToCanvas({
        source: {
          element: video,
          height: videoHeight,
          width: videoWidth,
        },
        destination: {
          canvas: canvasBufferRef.current,
          height: videoHeight,
          width: videoWidth,
        },
      });

      const detection = await FaceApiClient.detectFaces(canvasBufferRef.current, {
        inputSize: FACE_DETECTION_INPUT_SIZE,
        scoreThreshold: FACE_DETECTION_THRESHOLD,
      });
      const faceBox = detection.length === 1 ? detection[0].box : undefined;

      let horizontalAngle = 0;
      let landmarks = null;

      if (faceBox != null && faceCanvasBufferRef.current != null) {
        // Use Math.floor to avoid floats in canvas dimensions, which hurts performance.
        // https://developer.mozilla.org/en-US/docs/Web/API/Canvas_API/Tutorial/Optimizing_canvas#avoid_floating-point_coordinates_and_use_integers_instead
        const faceBoxWidth = Math.floor(faceBox.width);
        const faceBoxHeight = Math.floor(faceBox.height);

        drawImageToCanvas({
          source: {
            element: canvasBufferRef.current,
            height: faceBoxHeight,
            width: faceBoxWidth,
            x: Math.floor(faceBox.x),
            y: Math.floor(faceBox.y),
          },
          destination: {
            canvas: faceCanvasBufferRef.current,
            height: faceBoxHeight,
            width: faceBoxWidth,
          },
        });

        landmarks = await FaceApiClient.detectLandmarks(faceCanvasBufferRef.current);
        horizontalAngle = horizontalAngleFromLandmarks(landmarks);
      }

      return {
        canvas: canvasBufferRef.current,
        faceBox,
        faceCount: detection.length,
        faceDimensions: {
          width: faceBox != null ? faceBox.width : 0,
          height: faceBox != null ? faceBox.height : 0,
        },
        horizontalAngle,
        landmarks,
      };
    },
  };
};

type DrawImageParameters = {
  height: number;
  width: number;
  x?: number;
  y?: number;
};

const drawImageToCanvas = ({
  source,
  destination,
}: {
  source: {
    element: HTMLCanvasElement | HTMLVideoElement | null;
  } & DrawImageParameters;
  destination: {
    canvas: HTMLCanvasElement | null;
  } & DrawImageParameters;
}) => {
  if (source.element == null || destination.canvas == null) {
    return;
  }

  const { canvas } = destination;

  canvas.width = source.width;
  canvas.height = source.height;

  const canvasContext = canvas.getContext('2d', {
    willReadFrequently: true,
    alpha: false, // supporting transparency is more expensive
  });

  canvasContext?.clearRect(0, 0, destination.width, destination.height);

  if ((canvas.width === 0 && canvas.height === 0) || canvasContext == null) {
    throw new Error('Canvas getContext returned null during runPoseDetection');
  }

  canvasContext?.drawImage(
    source.element,
    source.x != null ? source.x : 0,
    source.y != null ? source.y : 0,
    source.width,
    source.height,
    destination.x != null ? destination.x : 0,
    destination.y != null ? destination.y : 0,
    destination.width,
    destination.height,
  );

  return destination;
};
